import React from 'react'
import { alpha, Box, IconButton, Typography } from '@mui/material'
import { IconSeeker, PopoverSeeker } from '@ui-seeker/core'

import { AskIrisButton } from '@components'
import { dsColorNew } from '@palette'
import { fmtDay, fromIso } from '../../../@services/dates'
import { money, signedMoney, signedPct } from '../../../@services/format'
import { weeklyCellEvidence } from '../../../@services/askEvidence'


const STAT = { min: 'Min', avg: 'Avg', max: 'Max' }
const Flag = ({ children, color, title }) => <Box component="span" title={title} sx={{ fontSize: 10, fontWeight: 700, color, border: `1px solid ${color}`, borderRadius: '4px', px: 0.4, lineHeight: '14px' }}>{children}</Box>

/**
 * The small detail of a "Week by week" cell (CU-869fkdhjv phase 3, UX C9), only for Iris users: the pattern of the
 * analyzers' cell popover (`PopoverSeeker`). Title "Provider · date · Week N", one line office · days · statistic,
 * the price and its change (L / H / !), and the full-width "Ask Iris" band at the foot, which closes the detail and
 * asks (C2). `open` = `{ anchor, office, row, date, week, cell }`.
 */
export default function WeeklyCellDetail({ open, stat, lor, daily, code, scope, onClose }) {
	if (!open) return null
	const { anchor, office, row, date, week, cell } = open
	const n = Number(String(week.key).slice(4))
	const up = cell.eur > 0, flat = cell.eur !== null && Math.round(cell.eur) === 0
	const changeColor = cell.eur === null ? dsColorNew.textSubtle : flat ? dsColorNew.textMuted : up ? dsColorNew.dangerRed : dsColorNew.successText
	const versus = week.first ? 'no previous week' : `vs ${week.versus ? week.versus.replace(/^vs /, '') : 'the week before'}`
	const getAsk = () => weeklyCellEvidence({ office, row, date, week, cell, stat, lor, daily, code, scope })

	return <PopoverSeeker
		anchorEl={anchor}
		onClose={onClose}
		anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
		transformOrigin={{ vertical: 'center', horizontal: 'left' }}
		arrowProps={{ sx: { top: '35%', left: -6 } }}
		slotProps={{ paper: { sx: { p: 0, width: 300, borderRadius: '12px', border: `1px solid ${dsColorNew.grayBorder}`, boxShadow: `0 16px 40px ${alpha(dsColorNew.navy, 0.16)}`, backgroundColor: dsColorNew.white } } }}
	>
		<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1.75, py: 1.5, borderBottom: `1px solid ${dsColorNew.grayBorder}` }}>
			<Box sx={{ width: 30, height: 30, borderRadius: '8px', flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', backgroundColor: dsColorNew.pillActiveBg, color: dsColorNew.appBlue }}>
				<IconSeeker icon={row.kind === 'median' ? 'chart-column' : 'car'} sx={{ fontSize: 13 }}/>
			</Box>
			<Typography component="h2" sx={{ fontSize: 14, fontWeight: 600, color: dsColorNew.textTitle, minWidth: 0 }}>
				{row.name} · {fmtDay(fromIso(date), false)} · {Number.isFinite(n) && n > 0 ? `Week ${n}` : week.label}
			</Typography>
			<IconButton aria-label="Close" onClick={e => { e.stopPropagation(); onClose() }} sx={{ ml: 'auto', width: 28, height: 28, borderRadius: '8px', color: dsColorNew.textSubtle }}>
				<IconSeeker icon="xmark" sx={{ fontSize: 13 }}/>
			</IconButton>
		</Box>
		<Box sx={{ px: 1.75, py: 1.25 }}>
			<Typography sx={{ fontSize: 13, color: dsColorNew.textDark }}>{office.label} · {lor} days · {STAT[stat] || 'Min'}{daily ? ' · daily price' : ''}</Typography>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mt: 0.5, fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>
				<b style={{ color: dsColorNew.textTitle }}>{money(cell.value, code)}</b>
				{cell.low && <Flag color={dsColorNew.successText} title="Cheapest of the providers shown">L</Flag>}
				{cell.high && <Flag color={dsColorNew.dangerRed} title="Dearest of the providers shown">H</Flag>}
				{cell.thin && <Box component="span" title="Fewer than 5 prices" sx={{ color: dsColorNew.noteAmber, fontWeight: 700 }}>!</Box>}
				<Box component="span" sx={{ color: changeColor, fontWeight: 600 }}>
					{cell.eur === null ? (cell.noPrev ? 'no prev. week' : versus) : `· ${flat ? '=' : up ? '▲' : '▼'} ${signedPct(cell.pct)} ${signedMoney(cell.eur, code)} ${versus}`}
				</Box>
			</Box>
		</Box>
		<Box sx={{ px: 1.75, py: 1.25, borderTop: `1px solid ${dsColorNew.grayBorder}`, '&:empty': { display: 'none' } }}>
			<AskIrisButton variant="block" tooltip="Ask Iris about this price" getAsk={getAsk} onBeforeAsk={onClose}/>
		</Box>
	</PopoverSeeker>
}
